import React, { useEffect, useState } from 'react';
import { DialogModal } from '../../../../../theme/components';
import {
  Box,
  Button,
  FormLabel,
  Stack,
  TextField,
  Typography,
  FormControl,
  CircularProgress,
} from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { ErrorText } from '../../../../../theme/components/Forms';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { postGroup } from '../../../../../redux/groups/actions/thunks';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import { useShowMessage } from '../../../../../util/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { GroupAPI } from '../../../../../redux/groups/api/GroupAPI';
import { BusinessRole } from '../../../redux/businessModel/model/model';
import StoerkIdAPI from '../../../../../redux/stoerkID/StoerkIdAPI';
import { getMyWorkspaceSelector } from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';
import AddressFormFields from '../../../theme/components/AddressFormFields.tsx/AddressFormFields';
import { isSCCService } from '../../../redux/rights/selectors/roles';

const initialValues = {
  name: '',
  street: '',
  zipCode: '',
  city: '',
  country: '',
};

interface AddStoreModalProps {
  showModal: boolean;
  closeModal: () => void;
  groupIdParent?: string;
  groupId?: string;
}

const AddStoreModal = (props: AddStoreModalProps) => {
  const { showModal, closeModal } = props;
  const polyglot = getPolyglot();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const showError = useShowErrorMessage();
  const showSuccess = useShowMessage();
  const navigate = useNavigate();
  const [getGroups] = GroupAPI.useLazyGetGroupsQuery();
  const [linkGroupToWorkspace] = StoerkIdAPI.useLinkGroupToWorkspaceMutation();
  const workspace = useAppSelector(getMyWorkspaceSelector);

  const params = useParams<{ groupId: string }>();

  const isAdmin = useAppSelector(isSCCService);
  const isNewCompany = Boolean(!params.groupId) && isAdmin;

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      isNewCompany
        ? 'freshwatch.company.name_required'
        : 'freshwatch.store.name_required'
    ),
    street: Yup.string().when([], {
      is: () => !isNewCompany,
      then: () => Yup.string().required('group.location.street_required'),
      otherwise: () => Yup.string().notRequired(),
    }),
    zipCode: Yup.string().when([], {
      is: () => !isNewCompany,
      then: () => Yup.string().required('group.location.zip_code_required'),
      otherwise: () => Yup.string().notRequired(),
    }),
    city: Yup.string().when([], {
      is: () => !isNewCompany,
      then: () => Yup.string().required('group.location.city_required'),
      otherwise: () => Yup.string().notRequired(),
    }),
    country: Yup.string().when([], {
      is: () => !isNewCompany,
      then: () => Yup.string().required('group.location.country_required'),
      otherwise: () => Yup.string().notRequired(),
    }),
  });

  useEffect(() => {
    formik.resetForm();
  }, [showModal]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      formik.setTouched({
        name: true,
        street: true,
        zipCode: true,
        city: true,
        country: true,
      });
      if (!formik.isValid) {
        return;
      }
      setIsSubmitting(true);
      try {
        const groupData: any = {
          name: values.name,
          parentGroupID: params.groupId ? params.groupId ?? '' : undefined,
          attributes: {
            contacts: JSON.stringify([]),
            BUSINESS_ROLE: isNewCompany
              ? BusinessRole.COMPANY
              : BusinessRole.STORE,
          },
        };
        if (!isNewCompany) {
          groupData.attributes['street'] = values.street;
          groupData.attributes['zipCode'] = values.zipCode;
          groupData.attributes['city'] = values.city;
          groupData.attributes['country'] = values.country;
        }
        await dispatch(postGroup(groupData, undefined));
        const { data: groups } = await getGroups(undefined);
        const newGroup = Object.values(groups?.entities.groups || {}).find(
          (group) =>
            group.name === values.name &&
            group.attributes?.BUSINESS_ROLE ===
              (isNewCompany ? BusinessRole.COMPANY : BusinessRole.STORE)
        );
        if (newGroup) {
          if (!isNewCompany) {
            navigate(`/merchandiser/${newGroup.id}`);
          } else {
            await linkGroupToWorkspace({
              groupId: newGroup.id,
              workspaceId: workspace?.id!,
            });
          }
        }
        showSuccess(
          polyglot.t(
            isNewCompany
              ? 'freshwatch.company.added_successfully'
              : 'freshwatch.store.added_successfully'
          )
        );
      } catch (error: any) {
        showError(
          polyglot.t(
            isNewCompany
              ? 'freshwatch.company.failed_to_add'
              : 'freshwatch.store.failed_to_add'
          )
        );
        return error;
      } finally {
        setIsSubmitting(false);
        formik.resetForm();
        closeModal();
        formik.resetForm();
      }
    },
  });

  return (
    <DialogModal
      open={showModal}
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          padding: 0,
        },
      }}
      buttonActions={
        <Box
          display="flex"
          gap="1rem"
          padding="2rem"
          justifyContent="space-around"
          width="100%"
        >
          <Button onClick={closeModal} variant="outlined" fullWidth>
            {polyglot.t('button.cancel')}
          </Button>
          <Button
            type="submit"
            onClick={() => formik.handleSubmit()}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              polyglot.t('button.ok')
            )}
          </Button>
        </Box>
      }
      sx={{
        '& .MuiDialogContent-root': {
          padding: '2rem',
        },
      }}
    >
      <Stack gap={2} padding="0 2rem">
        <Typography variant="h3" gutterBottom>
          {isNewCompany
            ? polyglot.t('freshwatch.company.add')
            : polyglot.t('freshwatch.store.add')}
        </Typography>
        <Stack display={'flex'} gap="1rem">
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              <FormControl
                fullWidth
                sx={{
                  gap: '0.5rem',
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
              >
                <FormLabel htmlFor="name" required>
                  {isNewCompany
                    ? polyglot.t('freshwatch.company.name')
                    : polyglot.t('freshwatch.store.name')}
                </FormLabel>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  placeholder={
                    isNewCompany
                      ? polyglot.t(
                          'freshwatch.company.company_name_placeholder'
                        )
                      : polyglot.t('freshwatch.store.store_name_placeholder')
                  }
                  type="text"
                  variant="outlined"
                  autoComplete="off"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={
                    formik.touched.name &&
                    formik.errors.name && (
                      <ErrorText>{polyglot.t(formik.errors.name)}</ErrorText>
                    )
                  }
                />
              </FormControl>
              {!isNewCompany && <AddressFormFields formik={formik} />}
            </Stack>
          </form>
        </Stack>
      </Stack>
    </DialogModal>
  );
};

export default AddStoreModal;
